import React from 'react';

const fmtCurrency = (n) => '$' + new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number.isFinite(n) ? n : 0);
const parseMoney = (s) => {
  const cleaned = (s || '').toString().replace(/[^0-9.\-]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

class ProfitGapQuestion extends React.Component {
  componentDidMount() {
    if (this.headingRef) this.headingRef.focus();
  }
  componentDidUpdate(prevProps) {
    if (prevProps.step !== this.props.step && this.headingRef) this.headingRef.focus();
  }

  renderStep0() {
    const { vertical, values, onChange, onNext, onBack } = this.props;
    const isPandC = vertical === 'p_and_c';
    const total = isPandC
      ? (values.recurring_revenue || 0) + (values.non_recurring_revenue || 0)
      : (values.fee_based_revenue || 0) + (values.trailer_revenue || 0) + (values.direct_revenue || 0) + (values.transactional_revenue || 0);

    return (
      <div className="flex justify-center bg-succession-surface px-4 pt-2 pb-6">
        <div className="w-full max-w-3xl">
          <div className="mb-4">
            <button type="button" onClick={onBack} className="text-succession-primary hover:underline" aria-label="Go back">← Back</button>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-succession-foreground mb-2" tabIndex="-1" ref={(el) => (this.headingRef = el)}>Confirm your revenue</h1>
          <p className="text-succession-muted mb-4">Edit line items as needed. We’ll compute your total revenue.</p>

          {isPandC ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-succession-muted mb-1">Recurring revenue</label>
                <input type="text" className="border border-succession-border rounded p-3 w-full" value={fmtCurrency(values.recurring_revenue || 0)} onChange={(e)=>onChange({ recurring_revenue: Math.max(0, parseMoney(e.target.value))})} />
              </div>
              <div>
                <label className="block text-sm text-succession-muted mb-1">Non‑recurring revenue</label>
                <input type="text" className="border border-succession-border rounded p-3 w-full" value={fmtCurrency(values.non_recurring_revenue || 0)} onChange={(e)=>onChange({ non_recurring_revenue: Math.max(0, parseMoney(e.target.value))})} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-succession-muted mb-1">Fee‑based revenue</label>
                <input type="text" className="border border-succession-border rounded p-3 w-full" value={fmtCurrency(values.fee_based_revenue || 0)} onChange={(e)=>onChange({ fee_based_revenue: Math.max(0, parseMoney(e.target.value))})} />
              </div>
              <div>
                <label className="block text-sm text-succession-muted mb-1">Trailer revenue</label>
                <input type="text" className="border border-succession-border rounded p-3 w-full" value={fmtCurrency(values.trailer_revenue || 0)} onChange={(e)=>onChange({ trailer_revenue: Math.max(0, parseMoney(e.target.value))})} />
              </div>
              <div>
                <label className="block text-sm text-succession-muted mb-1">Direct revenue</label>
                <input type="text" className="border border-succession-border rounded p-3 w-full" value={fmtCurrency(values.direct_revenue || 0)} onChange={(e)=>onChange({ direct_revenue: Math.max(0, parseMoney(e.target.value))})} />
              </div>
              <div>
                <label className="block text-sm text-succession-muted mb-1">Transactional revenue</label>
                <input type="text" className="border border-succession-border rounded p-3 w-full" value={fmtCurrency(values.transactional_revenue || 0)} onChange={(e)=>onChange({ transactional_revenue: Math.max(0, parseMoney(e.target.value))})} />
              </div>
            </div>
          )}

          <div className="mt-4 p-3 rounded border border-succession-border bg-white">
            <div className="text-succession-muted text-sm">Total revenue</div>
            <div className="text-2xl font-semibold text-succession-foreground">{fmtCurrency(total)}</div>
          </div>

          <div className="mt-6 flex justify-end">
            <button type="button" className="bg-succession-primary text-white px-4 py-2 rounded hover:bg-succession-primary/90" onClick={onNext} aria-label="Next">Next</button>
          </div>
        </div>
      </div>
    );
  }

  renderStep1() {
    const { totals, values, onChange, onNext, onBack } = this.props;
    return (
      <div className="flex justify-center bg-succession-surface px-4 pt-2 pb-6">
        <div className="w-full max-w-3xl">
          <div className="mb-4">
            <button type="button" onClick={onBack} className="text-succession-primary hover:underline" aria-label="Go back">← Back</button>
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-succession-foreground mb-2" tabIndex="-1" ref={(el) => (this.headingRef = el)}>Enter Normalized Expenses</h1>
          <p className="text-succession-muted mb-2">Tell us your “normalized” operating expenses.</p>
          <p className="text-succession-muted mb-2">This is what it costs to run your business in a typical year, without extras or unusual items. Please exclude:</p>
          <ul className="list-disc ml-6 text-succession-muted mb-2">
            <li>Personal or discretionary expenses (like car leases, family payroll, or travel perks)</li>
            <li>One-time or unusual costs (such as lawsuits, consulting projects, or office moves)</li>
            <li>Compensation that’s above or below normal market rates</li>
          </ul>
          <p className="text-succession-muted mb-4">By giving us this steady-state expense number, we can estimate your true operating profit (EBITDA) even if we don’t yet have your full accounting data.</p>

          <div>
            <label className="block text-sm text-succession-muted mb-1">Normalized operating expenses</label>
            <input type="text" className="border border-succession-border rounded p-3 w-full" value={fmtCurrency(values.normalized_operating_expenses || 0)} onChange={(e)=>onChange({ normalized_operating_expenses: Math.max(0, parseMoney(e.target.value)) })} />
          </div>

          <div className="mt-6 flex justify-end">
            <button type="button" className="bg-succession-primary text-white px-4 py-2 rounded hover:bg-succession-primary/90" onClick={onNext} aria-label="Next">Next</button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { step } = this.props;
    if (step === 0) return this.renderStep0();
    if (step === 1) return this.renderStep1();
    return null;
  }
}

export default ProfitGapQuestion;


